<script setup>
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import { computed } from 'vue'
import { useAuthStore } from '~/stores/auth'
import { useMessageStore } from '~/stores/messages'
import { signOut } from '~/composables/useMsal'

// Load user data
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    await centerDetailSuspense()
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Prepare form
const { isSubmitting, handleSubmit } = useForm()

// Auth
const authStore = useAuthStore()
const { updateUserStatus } = authStore
const { userStatus } = storeToRefs(authStore)

const isFormSubmitting = computed(() => isSubmitting.value || userStatus.value?.success)

const getFormClass = computed(() => {
    return {
        'is-submitting': isFormSubmitting.value
    }
})

const deleteButtonConfig = computed(() => {
    return {
        visible: true,
        label: staticKeys.value?.account_form_delete_button_label,
        buttonConfig: {
            type: 'submit',
            buttonType: 'primary',
            isPending: isFormSubmitting.value || userIsPending.value,
            large: true,
            active: true
        }
    }
})

// Callback for successful account deletion request
const userDeleteSuccessCallback = async () => {
    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.USER_DELETE,
        centerUid: centerDetails.value?.centerUid,
        centerName: centerDetails.value?.centerName
    })

    // Sign out user
    if (import.meta.client) {
        await signOut(route, `${route.path}?profile-delete-success`)
    }
}

// Callback for error account deletion request
const userDeleteErrorCallback = () => {
    updateUserStatus({
        success: false,
        message: staticKeys.value?.account_form_delete_error
    })
}

// Fetch mutation
const { mutate, isPending: userIsPending } = useDeleteProfileMutation({
    bearerToken,
    oid,
    onSuccessCallback: userDeleteSuccessCallback,
    onErrorCallback: userDeleteErrorCallback,
})

/**
 *  Handles form submit
 */
const onSubmit = handleSubmit(() => {
    // Fire delete request
    mutate()

    // Scroll to top sidebar if scroll container exists
    document.querySelector('.sidebar-content-inner')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
    })
})

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.ACCOUNT_DELETE,
    centerUid: centerDetails.value?.centerUid,
    centerName: centerDetails.value?.centerName
})

/**
 * Resets the status object with the given value.
 *
 * @param {any} value - The new value to set the status object to.
 * @return {void} This function does not return anything.
 */
const resetStatusObj = (value) => {
    updateUserStatus(value)
}

// Fade message out
watchEffect(() => {
    if (userStatus.value?.success) {
        // Hide message
        setTimeout(() => {
            updateUserStatus(null)
        }, 2000)
    }
})
</script>

<template>
    <SideBarWrapper>
        <div class="delete-account-content">
            <div class="delete-account-intro">
                <FormMessage :form-message-config="userStatus" @update:set-status="resetStatusObj" />

                <SideBarMainHeadline :sidebar-main-headline-config="{ title: staticKeys?.menu_deleteaccount_markdown }" />

                <p>{{ staticKeys?.account_form_delete_intro_1 }}</p>
                <p>{{ staticKeys?.account_form_delete_intro_2 }}</p>
                <p>{{ staticKeys?.account_form_delete_intro_3 }}</p>
            </div>
            <form class="delete-account-form" :class="getFormClass" @submit.prevent="onSubmit">
                <p>
                    {{ staticKeys?.account_form_delete_intro_4 }}
                </p>

                <FormButton :form-button-config="deleteButtonConfig" />
            </form>
        </div>
    </SideBarWrapper>
</template>

<style lang="scss">
.delete-account-content {
    display: flex;
    width: 100%;
    flex-flow: column;
    min-height: calc(100vh - 186px);
    text-align: center;
    position: relative;
    justify-content: space-between;

    @include bp(medium1) {
        min-height: calc(100vh - 160px);
    }

    p {
        @include body2-medium;
    }
}

.delete-account-form {
    &::after {
        content: '';
        transition: opacity 400ms ease;
        opacity: 0;
    }

    &.is-submitting {
        @include form-is-submitting;
    }
}
</style>
